import { OsTypes, osName } from "react-device-detect";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useReactRouter from "use-react-router";

import useAppsflyerOneLink, { getFallbackOnelinkUrl, staticOnelinkUrl, withAdSet, withCurrentPath } from "components/common/useAppsflyerOneLink";
import trackEvent, { type TrackLocation } from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";

/**
 * Resolves the href every store-download CTA should point at: always the attributed AppsFlyer
 * OneLink, never a raw apps.apple.com/play.google.com URL. OneLink does the OS-based store split
 * server-side, so one URL correctly routes both iOS and Android without this hook (or its
 * caller) having to pick a store itself.
 *
 * Prefers the live, Smart-Script-generated link (useAppsflyerOneLink()); getFallbackOnelinkUrl()
 * is used instead while that hasn't resolved yet, or never does at all -- ad blockers routinely
 * block appsflyersdk.com. That fallback still OS-smart-redirects and still carries pid/c/af_sub1,
 * so even an ad-blocked click keeps its AppsFlyer attribution instead of silently becoming an
 * untracked Organic install.
 *
 * Superseded 2026-09-15 (123yb6937h3, direct CTO instruction): this used to take a
 * `desktopFallback` argument and return it verbatim on desktop and, briefly, on every device
 * before the mount effect ran -- every caller passed APP_STORE_URL_IOS/ANDROID for that
 * argument, so a desktop visitor got an unattributed direct link, permanently. Verified against
 * production HTML on https://www.stippl.io/: 4 apps.apple.com links, 3 play.google.com links, 0
 * onelink.me. A direct store link loses install attribution -- it lands as untracked Organic --
 * so no user-facing download link may ever be one, full stop. There is no longer a desktop
 * branch here at all.
 *
 * Known trade-off this creates: OneLink's own desktop redirect can only point at one
 * destination, and for template cgUq that destination is the App Store (measured: `af_web_dp`
 * and `af_r` do not override it). So a desktop click on what used to be the Google Play badge
 * now lands on the App Store page instead. That is a real regression in that one spot, accepted
 * deliberately in exchange for never emitting an unattributed direct link -- see the comment
 * above the two badges in AppStoreButtons.tsx. The remedy is an AppsFlyer dashboard change (a
 * configured desktop fallback URL on template cgUq), not code.
 *
 * Update 2026-10-13: the rule above stands (OneLink on every device) and so does the OneLink's
 * deep link into an installed app. What changed is under it: since the AppsFlyer Zero Plan, OneLink no
 * longer passes campaign tokens on to the App Store, so an iPhone install says nothing about its surface.
 * iOS installs per surface now come from Apple Custom Product Pages. Every OneLink we build carries
 * `af_ios_store_cpp=<page id>` (customProductPage.ts, applied once at the end of each builder in
 * useAppsflyerOneLink.ts and in bioLinks.ts), which OneLink still forwards to the App Store as
 * `ppid=<id>` (verified 2026-10-13 with an iPhone user agent); App Store Connect reports downloads per
 * page. The id derives from the link's `c`, so it needs no device check and is identical on the server:
 *
 *   Invite page               join_link
 *   Trip share and previews   trip_share_page, web_route_preview, web_list_preview, profile_page
 *   Website                   web_home, web_feature_lp, web_discover, web_auth
 *   Blog                      web_blog
 *   Link in bio               links_* (bioLinks.ts)
 *   Other                     everything else, incl. web_other and any utm_campaign override
 *
 * Hydration safety: several callers of this hook (Home.tsx, HomeMarketing.tsx, Navigation.tsx)
 * are server-rendered, and both useAppsflyerOneLink() and getFallbackOnelinkUrl() read browser
 * state (the async Smart Script result, window.location) that either doesn't exist on the server
 * or hasn't settled by the very first client render -- calling either of them during that first
 * render would make the server and client markup disagree, and `hydrateRoot` tears the tree down
 * over exactly that kind of mismatch. So the pre-mount value is `staticOnelinkUrl(pathname)`,
 * where `pathname` comes from react-router's `useLocation()`: the server renders under
 * `StaticRouter` with the request path and the client under `BrowserRouter`, so `useLocation()`
 * resolves to the same path on both, and the two renders stay byte-identical -- the same
 * reasoning `LanguageSwitcher.tsx` and `MarketingShell.tsx` already rely on for their own
 * `useLocation()` calls. The URL only upgrades to the attributed link inside a `useEffect`, after
 * mount -- same useState + useEffect shape this hook always used to gate its device check, just
 * gating "has it mounted" instead of "is this a phone".
 */
export function useAppDownloadUrl(): string {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	// Called unconditionally (rules of hooks) even though its result is only used post-mount below --
	// it's cheap, and the Smart Script's own load/generate cycle is what's actually async here.
	const oneLinkUrl = useAppsflyerOneLink();
	// Also called unconditionally -- needed pre-mount below, but rules of hooks means it can't be
	// skipped once mounted either.
	const { pathname } = useLocation();

	if (!mounted) return staticOnelinkUrl(pathname);

	return oneLinkUrl || getFallbackOnelinkUrl();
}

/**
 * Same hydration contract as useAppDownloadUrl above (see its doc comment for the full reasoning),
 * for a caller that also needs its own `af_adset` placement tag on the URL -- SSR-rendered inline
 * install surfaces (InlineInstallCard, WaveDownloadCTA) that compute their own href/QR payload at
 * render time rather than going through useAppStoreLink's plain `appstoreUrl` below.
 *
 * Pre-mount: `withAdSet(staticOnelinkUrl(pathname), adset)` -- `pathname` from `useLocation()`,
 * same hydration-safe reasoning as `useAppDownloadUrl`. Post-mount: upgrades to the live OneLink
 * (af_sub1 rewritten to the current path) or, failing that, getFallbackOnelinkUrl() -- the same
 * fallback chain useAppDownloadUrl uses, with `adset` layered on top either way.
 */
export function usePlacementOnelinkUrl(adset: string): string {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	// Called unconditionally (rules of hooks) -- see useAppDownloadUrl's own comment on this.
	const oneLinkUrl = useAppsflyerOneLink();
	const { pathname } = useLocation();

	if (!mounted) return withAdSet(staticOnelinkUrl(pathname), adset);

	const baseUrl = oneLinkUrl ? withCurrentPath(oneLinkUrl, window.location.pathname) : getFallbackOnelinkUrl();
	return withAdSet(baseUrl, adset);
}

// All current callers live under `components/sharemode/`, hence the "share_screen" default.
// Pass an explicit `location` when using this hook from a non-share-mode surface.
const useAppStoreLink = (location: TrackLocation = "share_screen") => {
	const { match } = useReactRouter();
	const appstoreUrl = useAppDownloadUrl();

	const handleTrack = () => {
		if (osName === OsTypes.Android) {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, location);
		} else {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, location);
		}
	};

	return { appstoreUrl, handleTrack };
};

export default useAppStoreLink;
